import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../../../../ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../../ui/alert-dialog";
import { supabaseReservation } from "../../../../../integrations/supabase/client";
import { useToast } from "../../../../../hooks/use-toast";
import { format } from "date-fns";

interface Reservation {
  id: string;
  name: string;
  email: string;
  date: string;
  time: string;
  guests: number;
}

interface CancelReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: Reservation | null;
  onSuccess: () => void;
}

export function CancelReservationDialog({ open, onOpenChange, reservation, onSuccess }: CancelReservationDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const fetchRestaurantName = async () => {
    const { data } = await supabaseReservation.from("agent_config").select("restaurant_name").limit(1).maybeSingle();
    return data?.restaurant_name || "Restaurant";
  };

  const handleCancel = async () => {
    if (!reservation) return;

    setLoading(true);

    try {
      // Update status to cancelled
      const { error } = await supabaseReservation.from("reservations").update({ status: "cancelled" }).eq("id", reservation.id);

      if (error) throw error;

      // Send cancellation email
      const restaurantName = await fetchRestaurantName();
      await supabaseReservation.functions.invoke("send-reservation-confirmation", {
        body: {
          name: reservation.name,
          email: reservation.email,
          date: format(new Date(reservation.date), "EEEE, MMMM d, yyyy"),
          time: formatTimeDisplay(reservation.time),
          guests: reservation.guests,
          restaurantName,
          emailType: "cancellation",
        },
      });

      toast({
        title: "Reservation Cancelled",
        description: "The reservation has been cancelled and the client notified",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      toast({
        title: "Error",
        description: "Failed to cancel reservation",
        variant: "destructive",
      });
    }

    setLoading(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Reservation</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to cancel the reservation for <strong>{reservation?.name}</strong>? This will send a cancellation email to the guest.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep Reservation
          </Button>
          <Button variant="destructive" onClick={handleCancel} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Cancel Reservation
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
